/**
 * WALL FRAMING round (docs/MODELER.md "WALL FRAMING round") — the curated wall-framing
 * data family, the roofFraming.ts pattern applied to walls. Pure data + lookups, no
 * geometry — bim-geometry's wall framing generator and the wall schema's choice lists
 * both read from HERE so the curated set has exactly one home.
 *
 * Representation-grade only, the standing E2 guard: no blocking, bracing, nailing,
 * fire-stops, shear panels, or engineering, ever.
 *
 * STUD SIZE IS DERIVED, never specified: the assembly's structural-core thickness IS
 * the stud depth (a 2×6 wall frames with 5½″-deep studs because its core layer is
 * 5½″) — the only firm standards a wall TYPE carries are the stud spacing and, via
 * HEADER_RULES, how headers size to their span.
 */
import type { ResolvedElement } from './params'

/** stud module choices, inches on center (walls skip 19.2 — a roof-deck module) */
export const STUD_SPACINGS = [16, 24] as const

/** 2× stock actual width — stud plan width along the wall, and plate thickness
 *  (plates are the same stock laid flat: depth = the core, thickness 1½″) */
export const STUD_WIDTH_IN = 1.5
export const PLATE_THICKNESS_IN = 1.5

/**
 * The curated firm-standard HEADER rule table: clear span → member depth (4× solid
 * stock family; the header always fills the full core thickness across the wall, so
 * the rule only sizes its DEPTH). Representation-grade — a drawing convention, not a
 * load calculation; overridable per opening is the recorded future refinement.
 */
export interface HeaderRule {
  maxSpanFt: number
  label: string
  depthIn: number
}
export const HEADER_RULES: HeaderRule[] = [
  { maxSpanFt: 4, label: '4×6', depthIn: 5.5 },
  { maxSpanFt: 6, label: '4×8', depthIn: 7.25 },
  { maxSpanFt: 8, label: '4×10', depthIn: 9.25 },
  { maxSpanFt: Infinity, label: '4×12', depthIn: 11.25 },
]

export function headerRuleFor(spanFt: number): HeaderRule {
  return HEADER_RULES.find((r) => spanFt <= r.maxSpanFt) ?? HEADER_RULES[HEADER_RULES.length - 1]
}

/**
 * Does this wall's assembly have a WOOD structural core? Non-wood assemblies (CMU,
 * concrete, a wallIsStem wall) have NO framing derivation — the Show Framing toggle
 * simply no-ops for them. The signal is the resolved core-layer MATERIAL
 * (ResolvedElement.coreMaterial, materialized by resolveElement): its curated
 * `category: 'Wood'`, or — for materials created before the category was seeded —
 * a wood-family name ('2×6 Wood Stud + Batt', '2×4 Wood Stud', …). A wall whose core
 * material can't be resolved at all is treated as non-wood (fail to nothing, never
 * to phantom studs in a concrete wall).
 */
export function isWoodCoreWall(el: ResolvedElement): boolean {
  if (el.category !== 'wall' || !el.assembly) return false
  const m = el.coreMaterial
  if (!m) return false
  if (m.category === 'Wood') return true
  // WALL CORE DEFINITION round (2026-08-18): 'Metal Stud' would match \bstud\b —
  // metal is an explicit NON-wood core (no wood framing derivation; cold-formed
  // members are a possible later round). Category first, name fallback after.
  if (m.category === 'Metal' || /\bmetal\b/i.test(m.name)) return false
  return /\b(wood|stud|timber|lumber)\b/i.test(m.name)
}

/**
 * FRAMING SESSION fix round (Matt, 2026-08-13 — "add any concrete walls to the
 * foundation layer"): is this wall's structural CORE concrete (poured, CMU, or
 * masonry)? Mirrors isWoodCoreWall's signal exactly — the resolved core-layer
 * material's curated `category: 'Concrete'` (the same organizer tag `seed.ts`
 * already puts on the footing/stem library material), or a concrete-family
 * name fallback for materials predating the category. Covers both a poured
 * concrete/CMU wall modeled as a full wall element (a basement or garage wall)
 * and a `wallIsStem` wall (the wall IS the stem — already always concrete by
 * the schema's own rule) — either way, its BODY is foundation structure, not
 * a finish. Consumed only for VIEWPORT classification (the `|foundation`
 * groupKey token, wall/generate3d.ts) — never gates framing derivation
 * (isWoodCoreWall already does that; the two checks are independent, not
 * mutually exclusive complements, since a wall could in principle be neither).
 */
export function isConcreteCoreWall(el: ResolvedElement): boolean {
  if (el.category !== 'wall' || !el.assembly) return false
  const m = el.coreMaterial
  if (!m) return false
  if (m.category === 'Concrete') return true
  return /\b(concrete|cmu|masonry)\b/i.test(m.name)
}
